/**
 * Activity Feed
 * 
 * Scrollable list of recent location activity events with:
 * - Last 50 activities displayed
 * - Auto-scroll on new events (can be paused)
 * - Filterable by rack, operator, activity type
 * - Searchable by BinCode, ItemNo, OperatorId
 * - Click to navigate to location
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 7 Implementation
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Activity,
  Search,
  Filter,
  X,
  ChevronDown,
  Pause,
  Play,
  MapPin,
  User,
  Package,
  Clock
} from 'lucide-react';
import { Card } from '../../../common/Card';
import { LocationActivityEvent, ActivityType, ActivityTypeNames, ActivityTypeColors } from '../../../../types/locationActivity';

interface ActivityFeedProps {
  /** All events to display (will be filtered/sorted) */
  events: LocationActivityEvent[];
  
  /** Maximum number of events to display */
  maxEvents?: number;
  
  /** Auto-scroll to newest event */
  autoScroll?: boolean;
  
  /** Callback when user clicks on an event to navigate */
  onEventClick?: (event: LocationActivityEvent) => void;
}

interface FilterState {
  rack: number | null;
  operator: string | null;
  activityType: ActivityType | null;
  searchTerm: string;
}

/**
 * Format timestamp to readable format
 */
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const time = date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const today = new Date();
  
  if (date.toDateString() === today.toDateString()) {
    return time;
  }
  
  return `${date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' })} ${time}`;
};

/**
 * Get icon for activity type
 */
const getActivityIcon = (activityType: ActivityType) => {
  switch (activityType) {
    case ActivityType.Pick:
    case ActivityType.PutAway:
    case ActivityType.Receiving:
      return Package;
    case ActivityType.Transfer:
      return MapPin;
    case ActivityType.Counting:
    case ActivityType.CorrectionIn:
    case ActivityType.CorrectionOut:
      return Activity;
    default:
      return Activity;
  }
};

/**
 * Get background color for activity type
 */
const getActivityColor = (activityType: ActivityType): string => {
  const colorClass = ActivityTypeColors[activityType];
  // Convert Tailwind class to simpler version for feed
  return colorClass.replace('/30', '/10');
};

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  events,
  maxEvents = 50,
  autoScroll = true,
  onEventClick
}) => {
  const [filteredEvents, setFilteredEvents] = useState<LocationActivityEvent[]>([]);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    rack: null,
    operator: null,
    activityType: null,
    searchTerm: ''
  });
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastEventCountRef = useRef(0);

  /**
   * Apply filters and search to events
   */
  const applyFilters = (allEvents: LocationActivityEvent[]): LocationActivityEvent[] => {
    let filtered = [...allEvents];

    // Filter by rack
    if (filters.rack !== null) {
      filtered = filtered.filter(e => e.rackNumber === filters.rack);
    }

    // Filter by operator
    if (filters.operator) {
      filtered = filtered.filter(e => e.operatorId === filters.operator);
    }

    // Filter by activity type
    if (filters.activityType !== null) {
      filtered = filtered.filter(e => e.activityType === filters.activityType);
    }

    // Search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        e.binCode.toLowerCase().includes(searchLower) ||
        e.itemNo.toLowerCase().includes(searchLower) ||
        e.operatorId.toLowerCase().includes(searchLower)
      );
    }

    // Sort by timestamp (newest first)
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Limit to max events
    return filtered.slice(0, maxEvents);
  };

  /**
   * Update filtered events when events or filters change
   */
  useEffect(() => {
    const filtered = applyFilters(events);
    setFilteredEvents(filtered);
  }, [events, filters, maxEvents]);

  /**
   * Auto-scroll to bottom when new events arrive
   */
  useEffect(() => {
    if (autoScroll && !isAutoScrollPaused && scrollContainerRef.current) {
      // Only auto-scroll if new events were added
      if (filteredEvents.length > lastEventCountRef.current) {
        scrollContainerRef.current.scrollTop = 0; // Scroll to top (newest first)
      }
    }
    lastEventCountRef.current = filteredEvents.length;
  }, [filteredEvents, autoScroll, isAutoScrollPaused]);

  /**
   * Get unique values for filter dropdowns
   */
  const getUniqueRacks = (): number[] => {
    const racks = new Set(events.map(e => e.rackNumber));
    return Array.from(racks).sort((a, b) => a - b);
  };

  const getUniqueOperators = (): string[] => {
    const operators = new Set(events.map(e => e.operatorId));
    return Array.from(operators).sort();
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setFilters({
      rack: null,
      operator: null,
      activityType: null,
      searchTerm: ''
    });
  };

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = (): boolean => {
    return filters.rack !== null || 
           filters.operator !== null || 
           filters.activityType !== null || 
           filters.searchTerm !== '';
  };

  return (
    <Card className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="text-blue-600" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">Aktivitetsfeed</h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {filteredEvents.length} av {events.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Auto-scroll toggle */}
            <button
              onClick={() => setIsAutoScrollPaused(!isAutoScrollPaused)}
              className={`p-2 rounded-lg transition-colors ${
                isAutoScrollPaused
                  ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
              }`}
              title={isAutoScrollPaused ? 'Start auto-scroll' : 'Pause auto-scroll'}
            >
              {isAutoScrollPaused ? <Play size={16} /> : <Pause size={16} />}
            </button>
            
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                showFilters || hasActiveFilters()
                  ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Filter size={16} />
              <span className="text-sm font-medium">Filter</span>
              {hasActiveFilters() && (
                <span className="ml-1 bg-blue-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  !
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Søk etter lokasjon, vare eller operatør..."
            value={filters.searchTerm}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
            <div className="grid grid-cols-3 gap-2">
              {/* Rack filter */}
              <div>
                <label className="text-xs text-gray-600 block mb-1">Reol</label>
                <select
                  value={filters.rack || ''}
                  onChange={(e) => setFilters({ ...filters, rack: e.target.value ? Number(e.target.value) : null })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Alle reoler</option>
                  {getUniqueRacks().map(rack => (
                    <option key={rack} value={rack}>Reol {rack}</option>
                  ))}
                </select>
              </div>

              {/* Operator filter */}
              <div>
                <label className="text-xs text-gray-600 block mb-1">Operatør</label>
                <select
                  value={filters.operator || ''}
                  onChange={(e) => setFilters({ ...filters, operator: e.target.value || null })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Alle operatører</option>
                  {getUniqueOperators().map(op => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>

              {/* Activity type filter */}
              <div>
                <label className="text-xs text-gray-600 block mb-1">Type</label>
                <select
                  value={filters.activityType || ''}
                  onChange={(e) => setFilters({ ...filters, activityType: e.target.value ? Number(e.target.value) as ActivityType : null })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Alle typer</option>
                  {Object.entries(ActivityTypeNames).map(([key, name]) => (
                    <option key={key} value={key}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear filters button */}
            {hasActiveFilters() && (
              <button
                onClick={clearFilters}
                className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                <X size={14} />
                <span>Fjern filtre</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Events List */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-2"
        style={{ maxHeight: '600px' }}
      >
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Activity className="mx-auto mb-2 text-gray-400" size={32} />
            <p className="text-sm">
              {hasActiveFilters() ? 'Ingen hendelser matcher filtrene' : 'Ingen hendelser ennå'}
            </p>
          </div>
        ) : (
          filteredEvents.map((event, index) => {
            const Icon = getActivityIcon(event.activityType);
            const bgColor = getActivityColor(event.activityType);
            
            return (
              <div
                key={`${event.timestamp}-${index}`}
                onClick={() => onEventClick && onEventClick(event)}
                className={`p-3 rounded-lg border border-gray-200 ${bgColor} ${
                  onEventClick ? 'cursor-pointer hover:shadow-md' : ''
                } transition-all`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="p-2 bg-white rounded-lg">
                    <Icon size={16} className="text-gray-700" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 text-sm">
                            {event.binCode}
                          </span>
                          <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full">
                            {ActivityTypeNames[event.activityType]}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <div className="flex items-center gap-1">
                            <Package size={12} />
                            <span>{event.itemNo}</span>
                            <span className="text-gray-400">×</span>
                            <span className="font-medium">{event.quantity}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User size={12} />
                            <span>{event.operatorId}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 whitespace-nowrap">
                        <Clock size={12} />
                        <span>{formatTimestamp(event.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {filteredEvents.length > 0 && (
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600 text-center">
            Viser {filteredEvents.length} av {events.length} hendelser
            {isAutoScrollPaused && (
              <span className="ml-2 text-orange-600 font-medium">
                • Auto-scroll pauset
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ActivityFeed;
